import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import CommonFilterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../components/Common/TabBar";
import Deletepopup from "../../../components/Modal/Deletepopup";

// Updated custom hook paths
import Actions from "../../../components/Common/Actions";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import ListPageLayout from "../../../components/Layout";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

export const DoctorCommonHeads = [
  {
    name: "Gender",
    navigatePath: "/doctors-gender",
  },
  {
    name: "Experience",
    navigatePath: "/doctors-experience",
  },
  {
    name: "Languages Known",
    navigatePath: "/doctors-language",
  },
  {
    name: "Medical Council",
    navigatePath: "/doctors-medical-council",
  },
  {
    name: "Clinic Speciality",
    navigatePath: "/doctors-clinic-speciality",
  },
  {
    name: "Online Specialization",
    navigatePath: "/online-specialization",
  },
  {
    name: "Inclinic Specialization",
    navigatePath: "/inclinic-specializations",
  },
];

const DoctorsGender = () => {
  const navigate = useNavigate();
  const addNewGender = {
    id: "addnewpost",
    onClick: () => navigate("/doctor-gender-create"),
    style: { width: "240px" },
    label: "Add New Gender",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    Gender: [],
    Status: [],
  });
  const [filteringKey, setFilteringKey] = useState([]);
  const [tempSelectedValues, setTempSelectedValues] = useState({
    Gender: [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState("Gender");
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  const [loader, setLoader] = useState(true);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}doctors-gender/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "doctors-gender",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Gender",
      dataIndex: "type",
      key: "gender",
      fixed: "left",
    },
    {
      title: "Status",
      key: "status",
      width: 150,
      render: (text, record, index) => (record.active ? "Active" : "InActive"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 350,
      render: (_, record) => (
        <Actions
          isEdit={true}
          isView={true}
          isDelete={true}
          onClickView={() => navigate(`/doctor-gender-view/${record._id}`)}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() => navigate(`/doctor-gender-edit/${record._id}`)}
        />
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const filters = {
    Gender: ["Male", "Female"],
    Status: ["Active", "Inactive"],
  };

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "/add-new-doctors",
      },
      {
        text: "Common Heads",
        link: "/doctors-gender",
      },
      {
        text: "Gender",
        link: "",
      },
    ],
    [],
  );

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className=" healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewGender} />}
      titles={<NavBarTitles titles={DoctorCommonHeads} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => setIsFilterModalVisible(true)}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={() => {
          setSelectedValues(tempSelectedValues);
          setIsFilterModalVisible(false);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        onReset={() => {
          setTempSelectedValues({ Gender: [], Status: [] });
          setSelectedValues({ Gender: [], Status: [] });
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />
    </ListPageLayout>
  );
};

export default DoctorsGender;
